/**
 * Client portal (hostname / query) — shortlist privacy & talentpool scope.
 * Used by GlobalShortlist, KanbanBoard, TalentPool, and Supabase portal helpers.
 */

import { getVacancyLifecycleStatus } from '../components/VacancyList';
import { isEffectiveVoorgesteldStage } from './vacancyClientPortalStage';

/** When `fullPipelineVisibility` is false (alleen AIM), geen shortlist-fase kandidaten in portaal. */
export type PortalPrivacyOptions = {
    fullPipelineVisibility?: boolean;
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Klantportaal-organisatie AIM: beperkte pipeline (geen shortlist-status op vacature). */
export function isPortalAimRestrictedClient(client?: {
    name?: string | null;
    clientportal_custom_domain?: string | null;
} | null): boolean {
    const name = String(client?.name || '').trim();
    if (/^aim(\s|\(|$|\.)/i.test(name)) return true;
    const domain = String(client?.clientportal_custom_domain || '').trim().toLowerCase();
    if (domain && /^aim[\.\-]/.test(domain)) return true;
    if (typeof window !== 'undefined') {
        const host = window.location.hostname.toLowerCase();
        if (/^aim[\.\-]/.test(host) || host.includes('.aim.') || host.startsWith('aim.')) return true;
    }
    return false;
}

/** Standaard portaal: volledige pipeline; alleen AIM beperkt. */
export function resolvePortalFullPipelineVisibility(client?: {
    name?: string | null;
    clientportal_custom_domain?: string | null;
} | null): boolean {
    return !isPortalAimRestrictedClient(client);
}

/** Gearchiveerde vacatures niet tonen in klantportaal. */
export function isPortalArchivedVacancy(v: any): boolean {
    if (!v) return false;
    if (v?.data?.archived === true) return true;
    return getVacancyLifecycleStatus(v) === 'Archief';
}

export function filterPortalVisibleVacancies<T>(vacs: readonly T[]): T[] {
    return vacs.filter((v) => !isPortalArchivedVacancy(v));
}

export function portalPrivacyFromUser(user?: { portalFullPipelineVisibility?: boolean } | null): PortalPrivacyOptions {
    return { fullPipelineVisibility: user?.portalFullPipelineVisibility !== false };
}

function portalPipelineIsRestricted(options?: PortalPrivacyOptions): boolean {
    return options?.fullPipelineVisibility === false;
}

/** Hex-dash UUID shape (any version). Use before PostgREST `.in('vacancy_id', …)` to avoid 400 on bad values. */
const VACANCY_IN_FILTER_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Valid UUID for Supabase client / vacancy FK filters. */
export function isClientUuid(s: unknown): s is string {
    return typeof s === 'string' && UUID_RE.test(s);
}

/** Deduplicated ids safe for PostgREST `in.(uuid,…)` on `vacancy_id`. */
export function sanitizeVacancyIdsForInFilter(ids: readonly (string | undefined | null)[]): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const raw of ids) {
        const id = String(raw ?? '').trim();
        if (!id || !VACANCY_IN_FILTER_UUID_RE.test(id) || seen.has(id)) continue;
        seen.add(id);
        out.push(id);
    }
    return out;
}

/**
 * Vacancy belongs to the portal org when `opdrachtgever_id` of `eindklant_id` (kolom of `data.*`)
 * matches the ingelogde CRM-organisatie (o.a. Videoworks vaak als eindklant).
 */
export function vacancyLinkedToPortalOrgSet(v: any, orgIds: readonly string[] | null | undefined): boolean {
    const ids = (orgIds || [])
        .map((x) => String(x || '').trim().toLowerCase())
        .filter((x) => x.length > 0);
    if (!ids.length) return false;
    const norm = (s: string) => String(s || '').trim().toLowerCase();
    const og = norm(v?.opdrachtgever_id || v?.data?.opdrachtgever_id);
    const ek = norm(v?.eindklant_id || v?.data?.eindklant_id);
    return ids.some((id) => id && (og === id || ek === id));
}

/** @alias {@link vacancyLinkedToPortalOrgSet} */
export function vacancyLinkedToPortalOpdrachtgeverSet(
    v: any,
    orgIds: readonly string[] | null | undefined,
): boolean {
    return vacancyLinkedToPortalOrgSet(v, orgIds);
}

export function vacancyLinkedToPortalClientSet(v: any, allowedClientIds: readonly string[] | null | undefined): boolean {
    return vacancyLinkedToPortalOrgSet(v, allowedClientIds);
}

export function vacancyLinkedToPortalCompany(v: any, portalClientId: string | null | undefined): boolean {
    return vacancyLinkedToPortalOrgSet(v, portalClientId ? [portalClientId] : []);
}

/** Application row (with embedded `vacancies`) scoped to portal company (single id or org tree). */
export function applicationVacancyLinkedToPortalCompany(
    app: any,
    portalClientId: string | null | undefined,
    allowedClientIds?: readonly string[] | null,
): boolean {
    if (allowedClientIds?.length) {
        return vacancyLinkedToPortalClientSet(app?.vacancies, allowedClientIds);
    }
    return vacancyLinkedToPortalCompany(app?.vacancies, portalClientId);
}

/** Display name in shortlist / pipeline / pool lists (client portal). */
export function portalDisplayCandidateName(
    app: { status?: string | null; candidates?: { name?: string | null } | null; cv_snapshot?: unknown },
    isPortal: boolean,
    options?: PortalPrivacyOptions,
): string {
    if (!isPortal) return app.candidates?.name || 'Onbekend';
    if (isPortalHideCandidateNameForStatus(app.status, app, options)) return 'Kandidaat (vertrouwelijk)';
    return app.candidates?.name || 'Onbekend';
}

/** Mask real name in lists (shortlist / pre-funnel) — tenzij dossier al effectief «voorgesteld» i.v.m. portaal. */
export function isPortalHideCandidateNameForStatus(
    status: string | undefined | null,
    app?: { status?: string; cv_snapshot?: unknown } | null,
    options?: PortalPrivacyOptions,
): boolean {
    if (!portalPipelineIsRestricted(options)) return false;
    if (app && isEffectiveVoorgesteldStage({ status: String(app.status ?? status ?? ''), cv_snapshot: app.cv_snapshot })) {
        return false;
    }
    const s = String(status || '').toLowerCase().trim();
    if (!s) return false;
    if (/\bshortlist\b/.test(s) || s.includes('shortlist') || s.includes('op shortlist')) return true;
    if (s.includes('voorstellen')) return true;
    if (['new', 'nieuw', 'screening', 'concept'].includes(s)) return true;
    return false;
}

/** Shortlist / vroege funnel (vóór voorgesteld) — voor AIM uit pipeline & shortlist verbergen. */
export function isPortalPreVoorgesteldApplicationStatus(status: string | undefined | null): boolean {
    const s = String(status || '').toLowerCase().trim();
    if (!s) return false;
    if (/\bshortlist\b/.test(s) || s.includes('shortlist') || s.includes('op shortlist')) return true;
    if (s.includes('voorstellen')) return true;
    if (['new', 'nieuw', 'screening', 'concept', 'lead'].includes(s)) return true;
    return false;
}

/** AIM: kandidaat niet tonen in shortlist/pipeline als status nog shortlist-fase is. */
export function portalExcludeApplicationFromPipeline(
    app: { status?: string; cv_snapshot?: unknown } | null | undefined,
    options?: PortalPrivacyOptions,
): boolean {
    if (!portalPipelineIsRestricted(options)) return false;
    if (app && isEffectiveVoorgesteldStage({ status: String(app.status ?? ''), cv_snapshot: app.cv_snapshot })) {
        return false;
    }
    return isPortalPreVoorgesteldApplicationStatus(app?.status);
}

/** Block opening CandidateDetailModal for shortlist-phase applications — tenzij effectief voorgesteld via portaal. */
export function isPortalBlockCandidateDetailForStatus(
    status: string | undefined | null,
    app?: { status?: string; cv_snapshot?: unknown } | null,
    options?: PortalPrivacyOptions,
): boolean {
    if (!portalPipelineIsRestricted(options)) return false;
    if (app && isEffectiveVoorgesteldStage({ status: String(app.status ?? status ?? ''), cv_snapshot: app.cv_snapshot })) {
        return false;
    }
    const s = String(status || '').toLowerCase().trim();
    if (!s) return false;
    if (/\bshortlist\b/.test(s) || s.includes('shortlist') || s.includes('op shortlist')) return true;
    if (s.includes('voorstellen')) return true;
    if (['new', 'nieuw', 'screening', 'concept'].includes(s)) return true;
    return false;
}

/**
 * Talent pool / portal-scoped candidate IDs: only applications in these buckets
 * may expose the candidate (voorgesteld, geplaatst, rejected — not shortlist/gesprek).
 */
export function isPortalTalentPoolAllowedApplicationStatus(
    status: string | undefined | null,
    options?: PortalPrivacyOptions,
): boolean {
    const s = String(status || '').toLowerCase().trim();
    if (!s) return false;
    if (!portalPipelineIsRestricted(options)) return true;
    if (isPortalHideCandidateNameForStatus(s, null, options)) return false;
    if (/gesprek|interview|\b2e\b|\b3e\b|tweede|derde|vervolg|aanbod|\boffer\b|contract/.test(s)) return false;
    if (/voorgesteld|cv sent|submitted|proposed|draft_cv/.test(s)) return true;
    if (/plaatsing|geplaatst|placed|hired|bevestigd|aangenomen|succesvol/.test(s)) return true;
    if (/afgewezen|reject|niet aangenomen|niet geplaatst|closed|ingetrokken/.test(s)) return true;
    return false;
}
